import type { JSONContent } from '@tiptap/core';

const BlockTypesWithTitle = [
	'hubsDocumentComponent',
	'hubsLinkComponent',
	'hubsGalleryComponent',
] as const;
const BlockTypesWithDescription = [
	'hubsImageComponent',
	'hubsDocumentComponent',
	'hubsLinkComponent',
	'hubsGalleryComponent',
] as const;

type BlockTypeWithTitle = (typeof BlockTypesWithTitle)[number];
type BlockTypeWithDescription = (typeof BlockTypesWithDescription)[number];

const isBlockWithTitle = (editorContent: JSONContent) => {
	return BlockTypesWithTitle.includes((editorContent?.type ?? '') as BlockTypeWithTitle);
};
const isBlockWithDescription = (editorContent: JSONContent) => {
	return BlockTypesWithDescription.includes(
		(editorContent?.type ?? '') as BlockTypeWithDescription
	);
};

/**
 * @returns leading hubs block of type `hubs****`.
 */
const findLeadingHubsBlock = (editorContent: JSONContent[]): JSONContent | undefined => {
	const hubsBlocks = editorContent.filter((block) => block.type?.startsWith('hub'));

	return hubsBlocks.length && hubsBlocks[0] ? hubsBlocks[0] : undefined;
};

/**
 * @returns Any text from paragraph blocks that appears before a non-paragraph block
 * @example [{type="paragraph", text="Hello"}, {type="paragraph", text="World" }, {type="header", ...}] => "Hello/n/nWorld"
 * @example [{type="header", ...}, {type="paragraph", text="Hello"}, {type="paragraph", text="World" }, ] => ""
 */
const getPostContext = (editorContent: JSONContent[]) => {
	const firstNonParagraphIndex = editorContent.findIndex((block) => block.type !== 'paragraph');

	const paragraphBlocks = editorContent
		.slice(0, firstNonParagraphIndex)
		.filter((block) => block.type === 'paragraph');

	const context = paragraphBlocks
		.map((block) => block.content?.map((node) => node.text).join('') ?? '')
		.join('\n\n');

	return context;
};

const getPostTitle = (editorContent: JSONContent[]) => {
	const leadingHubBlock = findLeadingHubsBlock(editorContent);

	// the post title becomes the block title in the case of a leading hubs block
	// that inherently has its own title field (and the title field is non-empty).
	if (leadingHubBlock && isBlockWithTitle(leadingHubBlock)) {
		const blockTitle = leadingHubBlock.attrs?.post?.title ?? leadingHubBlock.attrs?.title ?? '';

		if (blockTitle) {
			return blockTitle;
		}
	}

	const firstHeadingBlock = editorContent.findIndex((item) => item.type === 'heading');

	return editorContent[firstHeadingBlock]?.content?.map((node) => node.text).join('') ?? '';
};

const getPostDescription = (editorContent: JSONContent[]) => {
	const leadingHubsBlock = findLeadingHubsBlock(editorContent);

	// the post description becomes the block description in the case of a leading hubs block
	// that inherently has its own description field (and the description field is non-empty).
	if (leadingHubsBlock && isBlockWithDescription(leadingHubsBlock)) {
		const blockDescription =
			leadingHubsBlock.attrs?.post?.description ?? leadingHubsBlock.attrs?.description ?? '';

		if (blockDescription) {
			return blockDescription;
		}
	}

	const firstNonParagraphIndex =
		editorContent.findIndex((block) => block.type !== 'paragraph') || 0;

	const nonContextParagraphBlocks = editorContent
		.slice(firstNonParagraphIndex)
		.filter((block) => block.type === 'paragraph');

	const description =
		nonContextParagraphBlocks
			.map((block) => block.content?.map((node) => node.text).join('') ?? '')
			.join('\n\n') ?? '';

	return description;
};

export const getPostAttributesFromEditorContent = (editorContent: JSONContent[]) => {
	const context = getPostContext(editorContent);
	const title = getPostTitle(editorContent);
	const description = getPostDescription(editorContent);

	return { title, description, context };
};
